/* eslint-disable react/prop-types */
import { LoadingButton } from '@mui/lab'
import { FileDownloadRounded } from '@mui/icons-material'
import {
  CircularProgress,
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  DialogActions,
  Grid,
  MenuItem,
  Tooltip,
} from '@mui/material'
import React from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useGlobalSnackbarStore } from '../../store/GlobalSnackBarStore'
import { useDialogOpened } from '../../utils/hooks/useDialogOpened'
import { ControlledSelectField } from '../common/ControlledSelectField'
import { ControlledDateField } from '../common/ControlledDateField'
import { isValidPastDateCoherence } from '../../utils/date/isValidPastDateCoherence'
import { blue, spacingItem } from '../theme'
import { ControlledAutoCompleteWithCustomValue } from '../common/ControlledAutocompleteWithCustomValue'
import { fullName } from '../../domain/Account/AccountDisplay'
import { useMutation } from 'react-query'
import { AnimalsClient } from '../../interactors/clients/AnimalsClient'
import { useAccountsStore } from '../../store/AccountsStore'
import dayjs from 'dayjs'
import { AccountDtoPermission } from '../../interactors/gen/backendClient'

type FormValues = {
  type: string
  dateToCheck: string
  startDate: string
  endDate: string
  adopterId: string
}

interface Props {
  menuItem?: boolean
  onBeforeOpen?: () => void
  hideTrigger?: boolean
  autoOpenSignal?: number | null
}

export const AnimalExportPopUp: React.FC<Props> = ({
  menuItem = false,
  onBeforeOpen,
  hideTrigger = false,
  autoOpenSignal,
}) => {
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const lastHandledAutoOpenSignalRef = React.useRef<number | null>(null)
  const globalSnackbarStore = useGlobalSnackbarStore()
  const accountsStore = useAccountsStore()
  const isReader =
    accountsStore.connectedAccount?.permission === AccountDtoPermission.Read ||
    accountsStore.connectedAccount?.permission === AccountDtoPermission.Editor

  const exportPdfMutation = useMutation(
    (variables: {
      accountId: string | undefined
      dateToCheck: string | undefined
      startDate: string | undefined
      endDate: string | undefined
    }) =>
      AnimalsClient.downloadAnimalsPdf(
        variables.accountId,
        variables.dateToCheck,
        variables.startDate,
        variables.endDate
      ),
    {
      onSuccess: async () => {
        globalSnackbarStore.triggerSuccessMessage(`PDF téléchargé !`)
      },
      onError: async (error) => {
        console.error(error)
        globalSnackbarStore.triggerErrorMessage(`Une erreur est survenue !`)
      },
    }
  )

  const exportRegisterExcelMutation = useMutation(
    (variables: {
      accountId: string | undefined
      dateToCheck: string | undefined
      startDate: string | undefined
      endDate: string | undefined
    }) =>
      AnimalsClient.downloadAnimalsRegisterExcel(
        variables.accountId,
        variables.dateToCheck,
        variables.startDate,
        variables.endDate
      ),
    {
      onSuccess: async () => {
        globalSnackbarStore.triggerSuccessMessage(`Excel téléchargé !`)
      },
      onError: async (error) => {
        console.error(error)
        globalSnackbarStore.triggerErrorMessage(`Une erreur est survenue !`)
      },
    }
  )

  const exportExcelMutation = useMutation(AnimalsClient.downloadAnimalsExcel, {
    onSuccess: async () => {
      globalSnackbarStore.triggerSuccessMessage(
        `Un lien de téléchargement vous sera envoyé par email dans quelques minutes.`
      )
    },
    onError: async (error) => {
      console.error(error)
      globalSnackbarStore.triggerErrorMessage(`Une erreur est survenue !`)
    },
  })

  const exportICADExcelMutation = useMutation(AnimalsClient.downloadAnimalsICADExcel, {
    onSuccess: async () => {
      globalSnackbarStore.triggerSuccessMessage(`Excel téléchargé !`)
    },
    onError: async (error) => {
      console.error(error)
      globalSnackbarStore.triggerErrorMessage(`Une erreur est survenue !`)
    },
  })

  const isExporting =
    exportPdfMutation.isLoading ||
    exportRegisterExcelMutation.isLoading ||
    exportExcelMutation.isLoading ||
    exportICADExcelMutation.isLoading

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      type: '',
      dateToCheck: 'noFilter',
      startDate: '',
      endDate: dayjs().toISOString(),
      adopterId: 'all',
    },
    mode: 'onChange',
  })

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    if (!isExporting) {
      const infoMessage =
        data.type === 'excel'
          ? `Export en cours... un lien de téléchargement sera envoyé par email dans quelques minutes.`
          : data.type === 'registerExcel' || data.type === 'icad' || data.type === 'pdf'
          ? `Export en cours... vous pouvez continuer de naviguer, le téléchargement démarrera automatiquement.`
          : `Export en cours... vous pouvez continuer de naviguer, le téléchargement démarrera automatiquement.`
      globalSnackbarStore.triggerInfoMessage(infoMessage)
    }
    if (data.type === 'pdf') {
      const accountId = data.adopterId === 'all' ? undefined : data.adopterId
      const dateToCheck = data.dateToCheck === 'noFilter' ? undefined : data.dateToCheck
      const startDate = data.dateToCheck === 'noFilter' ? undefined : data.startDate
      const endDate = data.dateToCheck === 'noFilter' ? undefined : data.endDate
      return exportPdfMutation.mutate({ accountId, dateToCheck, startDate, endDate })
    }
    if (data.type === 'registerExcel') {
      const accountId = data.adopterId === 'all' ? undefined : data.adopterId
      const dateToCheck = data.dateToCheck === 'noFilter' ? undefined : data.dateToCheck
      const startDate = data.dateToCheck === 'noFilter' ? undefined : data.startDate
      const endDate = data.dateToCheck === 'noFilter' ? undefined : data.endDate
      return exportRegisterExcelMutation.mutate({ accountId, dateToCheck, startDate, endDate })
    }
    if (data.type === 'excel') {
      return exportExcelMutation.mutate()
    }
    if (data.type === 'icad') {
      return exportICADExcelMutation.mutate()
    }
  }

  const allMembers = accountsStore.members
    ? accountsStore.members.map((member) => {
        const label = fullName(member)

        return { value: member.id, label, key: member.id }
      })
    : []

  const type = watch('type')
  const dateToCheck = watch('dateToCheck')
  const handleOpen = () => {
    onBeforeOpen?.()
    openDialog()
  }

  // Allows opening the dialog from a parent (e.g. a MenuItem) while keeping this component mounted.
  React.useEffect(() => {
    if (autoOpenSignal == null || autoOpenSignal === 0) return
    if (lastHandledAutoOpenSignalRef.current === autoOpenSignal) return
    lastHandledAutoOpenSignalRef.current = autoOpenSignal
    onBeforeOpen?.()
    openDialog()
  }, [autoOpenSignal, onBeforeOpen, openDialog])

  return (
    <>
      {!hideTrigger && (
        <Tooltip
          title={
            isReader ? `Votre permission ne vous permet pas d'effectuer un export. Rapprochez vous d'un admin` : ''
          }
        >
          {menuItem ? (
            <span>
              <MenuItem onClick={handleOpen} disabled={isReader}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <FileDownloadRounded fontSize="small" />
                  <Typography>Export</Typography>
                </Stack>
              </MenuItem>
            </span>
          ) : (
            <span>
              <Button
                color="primary"
                variant="contained"
                onClick={handleOpen}
                sx={{ marginTop: 4, paddingX: 3 }}
                disabled={isReader}
              >
                Exporter
              </Button>
            </span>
          )}
        </Tooltip>
      )}
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        PaperProps={{
          style: { borderRadius: 15, padding: 15 },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box>
              <Typography variant="h4" component="h2" marginBottom={2}>
                Exporter un document
              </Typography>
              {isExporting && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    {type === 'excel'
                      ? `Export en cours... un lien de téléchargement sera envoyé par email dans quelques minutes.`
                      : 'Export en cours... vous pouvez continuer de naviguer.'}
                  </Typography>
                </Box>
              )}

              <ControlledSelectField
                control={control}
                error={errors.type}
                fieldName="type"
                label="Type de fichier"
                options={[
                  {
                    label: 'Registre Entrées - Sorties (Excel)',
                    value: 'registerExcel',
                    locked: !accountsStore.isSubscribed(),
                  },
                  { label: 'Fiche Animal (Excel)', value: 'excel' },
                  { label: 'Cessions I-CAD à réaliser (Excel)', value: 'icad' },
                ]}
                size="small"
                requiredRule={'true'}
              />

              {(type === 'registerExcel' || type === 'pdf') && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, ...spacingItem }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <ControlledSelectField
                          control={control}
                          error={errors.dateToCheck}
                          fieldName="dateToCheck"
                          label="Sur la période"
                          options={[
                            { label: "Date d'entrée", value: 'entry' },
                            { label: 'Date de sortie', value: 'exit' },
                            { label: 'Pas de filtre', value: 'noFilter' },
                          ]}
                          size="small"
                          requiredRule={'true'}
                        />{' '}
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <ControlledDateField
                          control={control}
                          error={errors.startDate}
                          validate={(value) => isValidPastDateCoherence(value) || !value}
                          fieldName="startDate"
                          label="Du"
                          size="small"
                          requiredRule={dateToCheck === 'noFilter' ? undefined : 'La date de début est requise'}
                          disabled={dateToCheck === 'noFilter'}
                        />{' '}
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <ControlledDateField
                          control={control}
                          error={errors.endDate}
                          validate={(value) => isValidPastDateCoherence(value) || !value}
                          fieldName="endDate"
                          label="Au"
                          size="small"
                          requiredRule={dateToCheck === 'noFilter' ? undefined : 'La date de fin est requise'}
                          disabled={dateToCheck === 'noFilter'}
                        />{' '}
                      </Grid>
                    </Grid>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 2,
                      ...spacingItem,
                    }}
                  >
                    <Typography color={blue} sx={{ whiteSpace: 'nowrap' }}>
                      Exporter un document qui concerne
                    </Typography>
                    <ControlledAutoCompleteWithCustomValue
                      control={control}
                      error={errors.endDate}
                      fieldName="adopterId"
                      options={[
                        { label: 'Toutes les FA', value: 'all' },
                        ...allMembers.sort((a, b) => a.label.localeCompare(b.label)),
                      ]}
                      size="small"
                      defaultValue=""
                      requiredRule={'Ce champs est requis'}
                    />
                  </Box>
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button data-testid="cancel" onClick={closeDialog} sx={{ marginRight: 2 }}>
              Annuler
            </Button>
            <Tooltip
              title={
                type === 'icad' && !accountsStore.isSubscribed()
                  ? "Ce type d'export est réservé aux abonnés payants"
                  : ''
              }
            >
              <span>
                <LoadingButton
                  data-testid="apply-changes"
                  variant="contained"
                  type="submit"
                  disabled={
                    isExporting || ((type === 'registerExcel' || type === 'pdf') && !accountsStore.isSubscribed())
                  }
                  loading={isExporting}
                >
                  Exporter
                </LoadingButton>
              </span>
            </Tooltip>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
